import React, { useEffect, useMemo } from 'react'
import { Route, Routes, Navigate, useLocation } from 'react-router-dom'
import { useReactiveVar } from '@apollo/client'
import { pathOr } from 'ramda'
import { getToken } from 'apollo/client'
import { isUserActive } from 'apollo/variables'
import { getFromStorage, setToStorage } from 'utils/localStorage'
import Sso from 'pages/Sso'
import SignIn from 'pages/SignIn'
import SsoErrorPage from 'pages/SsoErrorPage'
import Main from 'containers/Main'
import config from 'constants/config'
import { ROOT, SIGN_IN, SSO, SSO_ERROR, EXTRA_ROUTES } from 'constants/routes'
import { useFlags } from 'launchdarkly-react-client-sdk'
import PropTypes from 'prop-types'
import { WebSocketProvider } from '@rightway/websocket-multiplexer'

import './App.css'

const { ENABLE_CUSTOM_LOGIN } = config

const MainContainer = ({ children }) => {
  const { useRtgEvents } = useFlags()
  const token = getToken()
  const { RTG_HOST, RTG_PROTOCOL } = config

  const isRTGEnabled = useMemo(
    () => useRtgEvents && RTG_HOST.length > 0,
    [useRtgEvents, RTG_HOST]
  )

  return (
    <WebSocketProvider
      options={{
        url: `${RTG_PROTOCOL}://${RTG_HOST}`,
        token,
        requiresToken: true,
        tokenEndpoint: `${RTG_PROTOCOL}://${RTG_HOST}/connection-tokens`,
        isEnabled: isRTGEnabled
      }}
    >
      {children}
    </WebSocketProvider>
  )
}

MainContainer.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]).isRequired
}

const App = () => {
  const active = useReactiveVar(isUserActive)
  const isTokenActive = getToken() || (!getToken() && !active)
  const location = useLocation()
  const pathname = pathOr(ROOT, ['pathname'], location)

  useEffect(() => {
    const originalLink = getFromStorage('originalLink')()
    if (!originalLink && !isTokenActive && !EXTRA_ROUTES.includes(pathname)) {
      setToStorage('originalLink')(pathname)
    }
  }, [isTokenActive, pathname])

  return isTokenActive ? (
    <MainContainer>
      <Main />
    </MainContainer>
  ) : (
    <Routes>
      {ENABLE_CUSTOM_LOGIN && <Route path={SIGN_IN} element={<SignIn />} />}
      <Route path={SSO} element={<Sso />} />
      <Route path={SSO_ERROR} element={<SsoErrorPage />} />
      <Route
        path="*"
        element={<Navigate to={ENABLE_CUSTOM_LOGIN ? SIGN_IN : SSO} />}
      />
    </Routes>
  )
}

export default App
